import React, { useState, useEffect, useMemo } from 'react';
import { X, Printer, Loader2 } from 'lucide-react';
import receiptsApi from './services/receiptsApi';

// ---------------------------------------------------------------------
// Thermal-printer preview for a single member's receipt (the "Print
// Receipt" button on each row of the table). Shows the member, their
// group, the total, and the real underlying transactions that make up
// this one receipt — a member can send more than one transaction toward
// a period, but still gets one receipt.
//
// `receipt` here is one of the type-split rows from
// GET /api/receipt-periods/members/ (period_member_receipts) — it has a
// real `receipt_no` but its own `id` is synthetic ("<receipt_no>-mpesa"
// or "<receipt_no>-cash"), since one backend Receipt can hold both cash
// and mpesa items for the same member+period. This dialog fetches the
// full receipt by receipt_no and filters down to just this row's
// payment type, so a mixed receipt still prints correctly as two
// separate customer copies (one opened from the Mpesa tab, one from Cash).
//
// Still a stub, per instruction: "Print Receipt" itself — that's where
// the thermal-printer integration eventually hooks in.
// ---------------------------------------------------------------------

const formatKSh = (amount) => `KSh ${Math.round(amount || 0).toLocaleString()}`;

// "5 Aug 2026, 10:32 AM"
const formatDateTime = (date) => {
  const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${datePart}, ${timePart}`;
};

const MemberReceiptDialog = ({ open, onClose, period, receipt, receipts }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !receipt) {
      setTransactions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    receiptsApi.fetchReceipt(receipt.receipt_no)
      .then((full) => {
        if (cancelled) return;
        const items = (full?.items || []).filter((item) =>
          receipt.type === 'cash' ? item.source_type === 'CASH' : item.source_type !== 'CASH'
        );
        setTransactions(items.map((item) => ({
          id: item.transaction_id,
          time: item.transaction_time ? new Date(item.transaction_time) : null,
          amount: item.amount,
        })));
      })
      .catch((err) => {
        console.error('Error loading receipt transactions:', err);
        if (!cancelled) setTransactions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, receipt?.receipt_no, receipt?.type]);

  // This member's #N — same order the Group Summary / Final Period table
  // uses, so a printed customer receipt can be matched back to its row.
  const memberNumber = useMemo(() => {
    if (!receipt || !receipts) return null;
    const idx = receipts.findIndex(r => r.id === receipt.id);
    return idx === -1 ? null : idx + 1;
  }, [receipt, receipts]);

  const handlePrint = () => {
    alert(`Print Receipt for ${receipt?.memberName} is not wired up yet — this is where the thermal printer integration hooks in.`);
  };

  if (!open || !period || !receipt) return null;

  const columnLabel = receipt.type === 'mpesa' ? 'MPESA' : 'CASH';

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
      <div className="relative z-10 rounded-lg shadow-xl max-w-sm w-full bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Member Receipt</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Thermal receipt "paper" — always black-on-white, monospace */}
        <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
          <div className="relative font-mono text-[13px] leading-snug text-black bg-white border border-dashed border-gray-300 rounded p-4">
            {memberNumber !== null && (
              <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black text-white text-[11px] font-bold flex items-center justify-center">
                #{memberNumber}
              </div>
            )}
            <div className="text-center">
              <p className="font-bold tracking-wide">LIFT SOLUTIONS</p>
              <p className="text-[10px] font-bold tracking-widest mt-1">CUSTOMER COPY</p>
            </div>
            <div className="border-t border-dashed border-gray-400 my-2"></div>

            <div className="flex justify-between"><span>Receipt No.</span><span className="font-semibold">{receipt.receipt_no}</span></div>
            <div className="flex justify-between"><span>Member</span><span className="text-right">{receipt.memberName}</span></div>
            <div className="flex justify-between"><span>Member ID</span><span>{receipt.memberId}</span></div>
            <div className="flex justify-between"><span>Group</span><span className="text-right">{period.group.name}</span></div>

            <div className="border-t border-dashed border-gray-400 my-2"></div>

            <div className="flex justify-between text-[15px] font-bold">
              <span>TOTAL</span>
              <span>{formatKSh(receipt.amount)}</span>
            </div>

            <div className="border-t border-dashed border-gray-400 my-2"></div>

            {loading ? (
              <div className="flex items-center justify-center py-4 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading transactions...
              </div>
            ) : (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-gray-400">
                  <th className="text-left py-1">TIME</th>
                  <th className="text-right py-1">{columnLabel}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id}>
                    <td className="py-0.5">{t.time ? formatDateTime(t.time) : '—'}</td>
                    <td className="py-0.5 text-right">{Math.round(t.amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-400 font-semibold">
                  <td className="pt-1">TOTAL</td>
                  <td className="pt-1 text-right">{Math.round(receipt.amount).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
            )}

            <div className="border-t border-dashed border-gray-400 my-2"></div>
            <p className="text-center text-[10px] text-gray-500">Thank you</p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberReceiptDialog;